import React from 'react';
import { AvmComp, AvmResult, PropertyDetailsType } from '../../utility/types';
import {
  formatUsd,
  getActiveSixAvgCdom,
  getAvmTopSix,
  getFifteenAvgCdom,
  getFifteenAvgScoreComp,
  getSoldSixAvgCdom,
} from '../../utility/utility_functions';
import { QuadDataDisplay, QuadDataPoint } from '../common/QuadDataDisplay';

const getArvLowPerSqFt = (
  result: AvmResult,
  propertyDetails: PropertyDetailsType
): string => {
  if (!result?.low_value || !propertyDetails?.sqft) {
    return 'N/A';
  }

  return formatUsd(result?.low_value / propertyDetails?.sqft, 0);
};

const getArvHighPerSqFt = (
  result: AvmResult,
  propertyDetails: PropertyDetailsType
): string => {
  if (!result?.high_value || !propertyDetails?.sqft) {
    return 'N/A';
  }

  return formatUsd(result?.high_value / propertyDetails?.sqft, 0);
};

const formatTopSoldCompsForDisplay = (
  result: AvmResult,
  comps: AvmComp[]
): QuadDataPoint[] => {
  if (!result || !comps) return [];
  return [
    {
      displayName: 'Sold Price',
      value: formatUsd(result?.closest_avg_sold_price, 0),
    },
    {
      displayName: '$/Sq Ft',
      value: formatUsd(result?.closest_avg_dollar_per_sqft, 0),
    },
    {
      displayName: 'Score Comp',
      value: result?.confidence_value?.toString() ?? 'N/A',
    },
    {
      displayName: 'CDOM',
      value: getSoldSixAvgCdom(comps),
    },
  ];
};

const formatActiveCompsForDisplay = (
  result: AvmResult,
  comps: AvmComp[]
): QuadDataPoint[] => {
  if (!result || !comps) return [];
  return [
    {
      displayName: 'List Price',
      value: formatUsd(result?.closest_avg_sold_price, 0),
    },
    {
      displayName: '$/Sq Ft',
      value: formatUsd(result?.closest_avg_dollar_per_sqft, 0),
    },
    {
      displayName: 'Score Comp',
      value: result?.confidence_value?.toString() ?? 'N/A',
    },
    {
      displayName: 'CDOM',
      value: getActiveSixAvgCdom(comps),
    },
  ];
};

const formatSoldCompsForDisplay = (
  result: AvmResult,
  comps: AvmComp[]
): QuadDataPoint[] => {
  if (!result || !comps) return [];
  return [
    {
      displayName: 'Sold Price',
      value: formatUsd(result?.all_comp_avg_sold_price, 0),
    },
    {
      displayName: '$/Sq Ft',
      value: formatUsd(result?.all_comp_dollar_per_sqft, 0),
    },
    {
      displayName: 'Score Comp',
      value: getFifteenAvgScoreComp(comps),
    },
    {
      displayName: 'CDOM',
      value: getFifteenAvgCdom(comps),
    },
  ];
};

const formatRepairValue = (
  result: AvmResult,
  propertyDetails: PropertyDetailsType
): QuadDataPoint[] => {
  if (!result || !propertyDetails) return [];
  return [
    {
      displayName: 'ARV Low',
      value: formatUsd(result.low_value, 0),
    },
    {
      displayName: 'ARV High',
      value: formatUsd(result.high_value, 0),
    },
    {
      displayName: 'ARV Low $/Sq Ft',
      value: getArvLowPerSqFt(result, propertyDetails),
    },
    {
      displayName: 'ARV High $/Sq Ft',
      value: getArvHighPerSqFt(result, propertyDetails),
    },
  ];
};

type CompsAndRepairsValueProps = {
  activeComps: { result: AvmResult; comps: AvmComp[] };
  soldComps: { result: AvmResult; comps: AvmComp[] };
  propertyDetails: PropertyDetailsType;
};

export const CompsAndRepairsValues = ({
  activeComps,
  soldComps,
  propertyDetails,
}: CompsAndRepairsValueProps) => {
  const topSixSoldComps = getAvmTopSix(soldComps);
  const topActiveComps = getAvmTopSix(activeComps);

  return (
    <div className="grid grid-cols-1 grid-rows-1 sm:grid-cols-2 sm:grid-rows-2 lg:grid-cols-4 lg:grid-rows-none gap-12 bg-slate-100 p-8">
      <QuadDataDisplay
        title="Top 6 Sold Comp Averages"
        data={formatTopSoldCompsForDisplay(soldComps?.result, topSixSoldComps)}
      />
      <QuadDataDisplay
        title="Active Comp Averages"
        data={formatActiveCompsForDisplay(activeComps?.result, topActiveComps)}
      />
      <QuadDataDisplay
        title="Top 15 Sold Comp Average"
        data={formatSoldCompsForDisplay(
          soldComps?.result,
          soldComps?.comps?.slice(0, 15)
        )}
      />
      <QuadDataDisplay
        title="After Repair Value"
        data={formatRepairValue(soldComps?.result, propertyDetails)}
      />
    </div>
  );
};
